import { TrendingUp, Sparkles, PiggyBank, ShoppingBag, Info, Download } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatRupiah } from "@/lib/dummyData";
import { ShuAPI } from "@/lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const EMPTY = { estimasi2025: 0, jasaModal: 0, jasaUsaha: 0, progress: 0, history: [] };

export default function SHUTracker() {
  const [data, setData] = useState(EMPTY);

  // Ambil SHU tracker nyata milik anggota dari backend (tanpa data dummy).
  useEffect(() => {
    ShuAPI.mine().then((d) => {
      setData({
        estimasi2025: Number(d.current?.amount || 0),
        jasaModal: Number(d.current?.jasa_modal || 0),
        jasaUsaha: Number(d.current?.jasa_usaha || 0),
        progress: d.current ? 78 : 0,
        history: (d.history || []).map((h) => ({ year: h.year, amount: Number(h.amount) })),
      });
    }).catch(() => setData(EMPTY));
  }, []);

  const total = (data.jasaModal + data.jasaUsaha) || 1;
  const breakdownPie = [
    { name: "Jasa Modal", value: data.jasaModal, color: "#2D5A27" },
    { name: "Jasa Usaha", value: data.jasaUsaha, color: "#D48C45" },
  ];

  return (
    <div className="space-y-7">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <div className="pill-tag bg-amber-warm/15 text-amber-700 mb-3">Pelacak SHU</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">SHU Tracker</h1>
          <p className="text-ink/75 mt-2">Sisa Hasil Usaha dihitung transparan dari jasa modal dan jasa usaha Anda.</p>
        </div>
        <Button onClick={() => toast.success("Bukti SHU diunduh")} variant="outline" className="rounded-full border-stone-soft" data-testid="shu-download">
          <Download size={15} /> Unduh Bukti SHU
        </Button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 rounded-3xl border-stone-soft p-7 bg-forest text-cream relative overflow-hidden grain-overlay">
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold">
              <Sparkles size={13} /> Estimasi SHU 2025
            </div>
            <div className="font-display font-bold text-5xl lg:text-6xl mt-3">{formatRupiah(data.estimasi2025)}</div>
            <p className="text-cream/80 mt-2">Dibagikan pada Rapat Anggota Tahunan · Maret 2026</p>

            <div className="mt-6">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-cream/80">Progress tahun berjalan</span>
                <span className="font-semibold text-amber-warm">{data.progress}%</span>
              </div>
              <Progress value={data.progress} className="h-2.5 bg-white/15" />
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
                <div className="text-[10px] uppercase tracking-wider text-amber-warm font-semibold">Jasa Modal</div>
                <div className="font-display font-bold text-2xl mt-1">{formatRupiah(data.jasaModal)}</div>
                <div className="text-xs text-cream/70 mt-1">{Math.round((data.jasaModal / total) * 100)}% dari estimasi total</div>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/15">
                <div className="text-[10px] uppercase tracking-wider text-amber-warm font-semibold">Jasa Usaha</div>
                <div className="font-display font-bold text-2xl mt-1">{formatRupiah(data.jasaUsaha)}</div>
                <div className="text-xs text-cream/70 mt-1">{Math.round((data.jasaUsaha / total) * 100)}% dari estimasi total</div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-5 rounded-3xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Komposisi SHU</h3>
          <div className="h-56 mt-3">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={breakdownPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {breakdownPie.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip content={({ active, payload }) => active && payload?.[0] ? <div className="bg-white border border-stone-soft rounded-lg px-3 py-2 text-xs shadow-md"><div className="font-semibold">{payload[0].name}</div><div className="text-forest">{formatRupiah(payload[0].value)}</div></div> : null} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl bg-cream p-4 mt-3 flex gap-2">
            <Info size={16} className="text-forest shrink-0 mt-0.5" />
            <p className="text-xs text-ink/75 leading-relaxed">
              SHU dihitung berdasarkan kontribusi simpanan (jasa modal) dan keaktifan belanja serta transaksi (jasa usaha) selama periode 1 tahun.
            </p>
          </div>
        </Card>
      </div>

      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink">Riwayat SHU Tahunan</h3>
        {data.history.length === 0 ? (
          <div className="py-14 text-center text-sm text-ink/55">Belum ada riwayat SHU. SHU dihitung setelah tutup buku tahunan.</div>
        ) : (
        <div className="h-72 mt-4">
          <ResponsiveContainer>
            <BarChart data={data.history} barCategoryGap={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
              <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#4A5248" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip content={({ active, payload }) => active && payload?.[0] ? <div className="bg-white border border-stone-soft rounded-lg px-3 py-2 text-xs shadow-md"><div className="font-semibold">{payload[0].payload.year}</div><div className="text-forest">{formatRupiah(payload[0].value)}</div></div> : null} />
              <Bar dataKey="amount" radius={[12, 12, 0, 0]}>
                {data.history.map((d, i) => <Cell key={i} fill={i === data.history.length - 1 ? "#D48C45" : "#2D5A27"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </Card>

      {data.history.length > 0 && (
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4">Detail Riwayat SHU</h3>
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft">
              <TableHead className="text-[10px] uppercase tracking-wider">Tahun</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Jumlah</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...data.history].reverse().map((h, i) => (
              <TableRow key={h.year} className="border-stone-soft" data-testid={`shu-history-${h.year}`}>
                <TableCell className="font-semibold text-ink">{h.year}</TableCell>
                <TableCell>
                  {i === 0 ? (
                    <Badge className="bg-amber-warm/20 text-amber-700 hover:bg-amber-warm/20 border-0 rounded-full">Estimasi</Badge>
                  ) : (
                    <Badge className="bg-sage/20 text-forest hover:bg-sage/20 border-0 rounded-full">Sudah Dibagi</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(h.amount)}</TableCell>
                <TableCell className="text-right">
                  <button className="text-forest text-sm font-semibold hover:underline">Lihat detail</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      )}
    </div>
  );
}
